import { type DisputeReason, resolveReason } from "@redotech/redo-dispute-db-schema/dispute-definition";

export const evidenceColumns = [
  "activity_log",
  "cancellation_policy_disclosure",
  "cancellation_rebuttal",
  "refund_policy_disclosure",
  "refund_refusal_explanation",
  "product_description",
  "invalid_dispute_reason",
  "cancellation_policy_file_url",
  "customer_communication_file_url",
  "refund_policy_file_url",
  "service_documentation_file_url",
  "shipping_documentation_file_url",
  "affidavit_file_url",
  "billing_agreement_file_url",
  "order_details_file_url",
  "terms_acceptance_file_url",
  "summary_file_url",
  "tracking_info",
] as const;

export type EvidenceColumn = (typeof evidenceColumns)[number];

export type CanonicalDisputeReason =
  | "product_not_received"
  | "product_not_as_described"
  | "fraudulent"
  | "credit_not_processed"
  | "duplicate"
  | "subscription_cancelled"
  | "unrecognized"
  | "incorrect_amount"
  | "payment_by_other_means"
  | "general"
  | "bank_processing_error";

export const REASON_EVIDENCE: Record<
  CanonicalDisputeReason,
  readonly EvidenceColumn[]
> = {
  product_not_received: [
    "activity_log",
    "shipping_documentation_file_url",
    "tracking_info",
    "customer_communication_file_url",
    "order_details_file_url",
    "summary_file_url",
  ],
  product_not_as_described: [
    "activity_log",
    "product_description",
    "service_documentation_file_url",
    "customer_communication_file_url",
    "summary_file_url",
  ],
  fraudulent: [
    "affidavit_file_url",
    "activity_log",
    "shipping_documentation_file_url",
    "tracking_info",
    "customer_communication_file_url",
    "terms_acceptance_file_url",
    "summary_file_url",
  ],
  credit_not_processed: [
    "refund_policy_file_url",
    "refund_policy_disclosure",
    "refund_refusal_explanation",
    "customer_communication_file_url",
    "activity_log",
    "summary_file_url",
  ],
  duplicate: ["order_details_file_url", "activity_log", "summary_file_url"],
  subscription_cancelled: [
    "cancellation_policy_file_url",
    "cancellation_policy_disclosure",
    "cancellation_rebuttal",
    "billing_agreement_file_url",
    "customer_communication_file_url",
    "summary_file_url",
  ],
  unrecognized: [
    "affidavit_file_url",
    "activity_log",
    "shipping_documentation_file_url",
    "tracking_info",
    "customer_communication_file_url",
    "terms_acceptance_file_url",
    "summary_file_url",
  ],
  incorrect_amount: [
    "order_details_file_url",
    "customer_communication_file_url",
    "activity_log",
    "summary_file_url",
  ],
  payment_by_other_means: [
    "order_details_file_url",
    "customer_communication_file_url",
    "activity_log",
    "summary_file_url",
  ],
  general: [
    "activity_log",
    "customer_communication_file_url",
    "order_details_file_url",
    "summary_file_url",
  ],
  bank_processing_error: [
    "activity_log",
    "order_details_file_url",
    "summary_file_url",
  ],
};

const REASON_EVIDENCE_LOOKUP: Partial<
  Record<DisputeReason, readonly EvidenceColumn[]>
> = REASON_EVIDENCE;

export function getEvidenceColumnsForReason(
  reason: DisputeReason,
): readonly EvidenceColumn[] {
  const resolved = resolveReason(reason);
  return REASON_EVIDENCE_LOOKUP[resolved] ?? REASON_EVIDENCE.general;
}
